import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCat = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={currentCat ? currentCat.name : '分类'}
        description={currentCat ? currentCat.description : siteInfo.description}
        keywords={`蜜桃成熟时,${currentCat?.name || ''},香港经典电影`}
        canonicalUrl={`${siteInfo.url}/category/${slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: currentCat?.name || '分类',
          description: currentCat?.description || '',
          url: `${siteInfo.url}/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: siteInfo.name,
            url: siteInfo.url,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-8 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCat?.name || slug || '' },
              ]}
            />

            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {currentCat?.name || slug}
              </h1>
              <p className="mt-2 text-sm text-foreground-600">{currentCat?.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-amber-500 text-white'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-amber-400/50'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                        {currentCat?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h2 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-amber-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        阅读全文 <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-amber-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              <Link to="/" className="mt-4 inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium">
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          )}

          <div className="mt-16 pt-10 border-t border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-950 mb-6">猜你喜欢</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {articles
                .filter((a) => a.category !== slug)
                .slice(0, 4)
                .map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                      <h4 className="mt-2 text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug">
                        {article.title}
                      </h4>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}